import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Check, Image as ImageIcon, Loader2, Upload, Trash2, AlertCircle } from 'lucide-react';
import bannerService, { OrganizationBanner } from '../../services/api/bannerService';
import { useConfirm } from '../tasky/modal';

// 旧 BannerLibraryModal.scss を内製化（Wave 3d / T20）。
// `.modal-header` / `.modal-content` / `.modal-footer` / `.empty-state` は
// Modal.scss・ProductSelectionModal.scss・ProductImageSelectionModal.scss が bare で
// 定義しており、本モーダルにも漏れて効いていた。削除後も現状描画を保つため、
// header の #fafafa 背景と上角丸、content の flex-col / スクロールバー装飾、
// empty-state の gap/flex、footer のモバイル縦積みも込みで内製化している。
// `.error-message` と `.spinner` は globals.css / ImageViewer.css 側に定義が
// 残るため、クラス名は残したまま上書き分だけをユーティリティで表現している。

export interface SelectedBanner {
  id: number;
  url: string;
  s3Key: string;
  fileName: string;
}

interface BannerLibraryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (selectedBanners: SelectedBanner[]) => void;
  maxSelection?: number;
}

const ACCEPTED_FILE_TYPES = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/webp', 'image/svg+xml'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const BannerLibraryModal: React.FC<BannerLibraryModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  maxSelection = 1,
}) => {
  const confirm = useConfirm();
  const [banners, setBanners] = useState<OrganizationBanner[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedBanners, setSelectedBanners] = useState<SelectedBanner[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch banners when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchBanners();
    } else {
      // Reset state when modal closes
      setSelectedBanners([]);
      setError(null);
      setIsDragging(false);
      setUploadProgress(null);
    }
  }, [isOpen]);

  const fetchBanners = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await bannerService.getBanners();
      setBanners(response.data);
    } catch (err) {
      console.error('Failed to fetch banners:', err);
      setError('バナーの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const validateFiles = (files: File[]): { valid: File[]; errors: string[] } => {
    const valid: File[] = [];
    const errors: string[] = [];

    files.forEach((file) => {
      if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
        errors.push(`${file.name}: 対応していないファイル形式です`);
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        errors.push(`${file.name}: ファイルサイズが大きすぎます（最大10MB）`);
        return;
      }
      valid.push(file);
    });

    return { valid, errors };
  };

  const handleUpload = async (files: File[]) => {
    const { valid, errors } = validateFiles(files);

    if (errors.length > 0) {
      setError(errors.join('\n'));
    }

    if (valid.length === 0) {
      return;
    }

    setUploading(true);
    setUploadProgress(`${valid.length}件のバナーをアップロード中...`);

    try {
      if (valid.length === 1) {
        await bannerService.uploadBanner(valid[0]);
      } else {
        const response = await bannerService.uploadBannersBatch(valid);
        if (response.errors.length > 0) {
          const uploadErrors = response.errors.map((e) => `${e.file_name}: ${e.error}`);
          setError(uploadErrors.join('\n'));
        }
      }

      // Refresh banners list
      await fetchBanners();
      setUploadProgress(null);
    } catch (err: any) {
      console.error('Failed to upload banners:', err);
      setError(err?.message || 'バナーのアップロードに失敗しました');
    } finally {
      setUploading(false);
      setUploadProgress(null);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      handleUpload(files);
    }
    // Reset input so same files can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleUpload(files);
    }
  }, []);

  const handleBannerToggle = useCallback((banner: OrganizationBanner) => {
    setSelectedBanners((prev) => {
      const existingIndex = prev.findIndex((b) => b.id === banner.id);

      if (existingIndex >= 0) {
        // Remove if already selected
        return prev.filter((b) => b.id !== banner.id);
      } else {
        // Add if not at max selection
        if (prev.length >= maxSelection) {
          // Replace the first selection if at max and maxSelection is 1
          if (maxSelection === 1) {
            return [{
              id: banner.id,
              url: banner.url,
              s3Key: banner.file_path,
              fileName: banner.file_name,
            }];
          }
          return prev;
        }
        return [...prev, {
          id: banner.id,
          url: banner.url,
          s3Key: banner.file_path,
          fileName: banner.file_name,
        }];
      }
    });
  }, [maxSelection]);

  const handleDeleteBanner = async (bannerId: number, e: React.MouseEvent) => {
    e.stopPropagation();

    if (!(await confirm({ title: 'このバナーを削除しますか？', tone: 'destructive', confirmText: '削除する' }))) {
      return;
    }

    try {
      await bannerService.deleteBanner(bannerId);
      setBanners((prev) => prev.filter((b) => b.id !== bannerId));
      setSelectedBanners((prev) => prev.filter((b) => b.id !== bannerId));
    } catch (err) {
      console.error('Failed to delete banner:', err);
      setError('バナーの削除に失敗しました');
    }
  };

  const handleConfirm = useCallback(() => {
    onSelect(selectedBanners);
    onClose();
  }, [selectedBanners, onSelect, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-6"
      onClick={onClose}
    >
      <div
        className="flex max-h-[85vh] w-full max-w-[900px] flex-col overflow-hidden rounded-2xl bg-white shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="modal-header flex shrink-0 items-center justify-between rounded-t-lg border-b border-b-gray-200 bg-[#fafafa] px-6 py-5">
          <div className="flex items-center gap-3">
            <h2 className="flex items-center gap-2 text-[18px] font-semibold text-gray-800">自社バナーライブラリ</h2>
          </div>
          <button
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border-none bg-transparent text-gray-500 transition-all duration-200 ease-[ease] hover:bg-gray-100 hover:text-gray-800"
            onClick={onClose}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="modal-content flex min-h-0 w-full max-w-full flex-1 flex-col overflow-y-auto overflow-x-hidden p-6 [scrollbar-color:#c1c1c1_#f1f1f1] [scrollbar-width:thin] [&::-webkit-scrollbar-thumb:hover]:bg-[#a8a8a8] [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-thumb]:bg-[#c1c1c1] [&::-webkit-scrollbar-track]:rounded [&::-webkit-scrollbar-track]:bg-[#f1f1f1] [&::-webkit-scrollbar]:w-2">
          {error && (
            <div className="error-message mb-4 flex items-start justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
              <AlertCircle size={16} className="mt-0.5 shrink-0 text-red-600" />
              <span className="flex-1 whitespace-pre-wrap text-[14px] text-red-600">{error}</span>
              <button
                className="shrink-0 cursor-pointer rounded-md border-none bg-red-600 px-3 py-1.5 text-[13px] text-white transition-[background] duration-200 ease-[ease] hover:bg-red-700"
                onClick={() => setError(null)}
              >
                閉じる
              </button>
            </div>
          )}

          {/* Upload Area */}
          <div
            className={
              uploading
                ? 'uploading pointer-events-none mb-6 flex cursor-wait flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 px-6 py-10 transition-all duration-200 ease-[ease]'
                : isDragging
                ? 'dragging group mb-6 flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-solid border-blue-500 bg-blue-100 px-6 py-10 transition-all duration-200 ease-[ease]'
                : 'group mb-6 flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 px-6 py-10 transition-all duration-200 ease-[ease] hover:border-blue-500 hover:bg-blue-50'
            }
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => !uploading && fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ACCEPTED_FILE_TYPES.join(',')}
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            {uploading ? (
              <>
                <Loader2 className="spinner mb-3 animate-spin text-blue-500" size={32} />
                <p className="text-[14px] font-medium text-gray-500">{uploadProgress || 'アップロード中...'}</p>
              </>
            ) : (
              <>
                <Upload
                  size={32}
                  className={isDragging ? 'mb-3 text-blue-500' : 'mb-3 text-gray-400 group-hover:text-blue-500'}
                />
                <p className="text-[14px] font-medium text-gray-500">クリックまたはドラッグ&ドロップでバナーをアップロード</p>
                <span className="mt-2 text-[12px] text-gray-400">JPEG, PNG, GIF, WebP, SVG / 最大10MB / 複数選択可</span>
              </>
            )}
          </div>

          {/* Banners Grid */}
          <div className="banners-section">
            <h3 className="section-title mb-4 flex items-center gap-2 text-[15px] font-semibold text-gray-800">
              アップロード済みバナー
              <span className="count rounded-[10px] bg-gray-200 px-2 py-0.5 text-[12px] font-medium text-gray-500">{banners.length}</span>
            </h3>

            {loading ? (
              <div className="loading-state flex flex-col items-center justify-center px-6 py-16 text-gray-500">
                <Loader2 className="spinner mb-4 animate-spin text-blue-500" size={32} />
                <p className="text-[14px]">バナーを読み込み中...</p>
              </div>
            ) : banners.length === 0 ? (
              <div className="empty-state flex flex-1 flex-col items-center justify-center gap-3 px-6 py-16 text-[14px] text-gray-400">
                <ImageIcon size={48} className="mb-4 opacity-50" />
                <p>アップロード済みのバナーがありません</p>
              </div>
            ) : (
              <div className="grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-4">
                {banners.map((banner) => {
                  const isSelected = selectedBanners.some((b) => b.id === banner.id);
                  return (
                    <div
                      key={banner.id}
                      className={
                        isSelected
                          ? 'selected cursor-pointer overflow-hidden rounded-xl border-2 border-blue-500 bg-white shadow-[0_0_0_3px_rgba(59,130,246,0.2)] transition-all duration-200 ease-[ease]'
                          : 'cursor-pointer overflow-hidden rounded-xl border-2 border-gray-200 bg-white transition-all duration-200 ease-[ease] hover:border-blue-500 hover:shadow-[0_4px_12px_rgba(0,0,0,0.1)]'
                      }
                      onClick={() => handleBannerToggle(banner)}
                    >
                      <div className="relative aspect-square w-full bg-[#f5f5f5]">
                        <img
                          src={banner.url}
                          alt={banner.title || banner.file_name}
                          className="h-full w-full object-contain"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            // 旧 `.image-placeholder.show-placeholder { display: flex }` の代替。
                            // Tailwind では `hidden` が `flex` に勝つため hidden を外してから flex を付ける。
                            target.nextElementSibling?.classList.remove('hidden');
                            target.nextElementSibling?.classList.add('flex');
                          }}
                        />
                        <div className="image-placeholder absolute inset-0 hidden items-center justify-center bg-[#f5f5f5] text-center text-[12px] text-gray-400">画像を読み込めません</div>
                        {isSelected && (
                          <div className="absolute inset-0 flex items-center justify-center bg-blue-500/60 text-white">
                            <Check size={24} />
                          </div>
                        )}
                        <button
                          className="absolute right-2 top-2 flex h-7 w-7 cursor-pointer items-center justify-center rounded-md border-none bg-white/90 text-red-600 opacity-70 shadow-[0_1px_3px_rgba(0,0,0,0.1)] transition-all duration-200 ease-[ease] hover:bg-red-50 hover:opacity-100 hover:shadow-[0_2px_6px_rgba(0,0,0,0.15)]"
                          onClick={(e) => handleDeleteBanner(banner.id, e)}
                          title="削除"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                      <div className="banner-info p-2.5">
                        <p className="overflow-hidden text-ellipsis whitespace-nowrap text-[12px] text-gray-800">{banner.title || banner.file_name}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer flex shrink-0 items-center justify-between gap-4 border-t border-t-gray-200 bg-gray-50 px-6 py-4 max-[768px]:flex-col">
          {selectedBanners.length > 0 && (
            <div className="flex min-w-0 flex-1 items-center gap-3">
              <span className="shrink-0 text-[13px] text-gray-500">選択中:</span>
              <div className="flex gap-2 overflow-x-auto">
                {selectedBanners.map((banner) => (
                  <div key={banner.id} className="h-[60px] w-[60px] shrink-0 overflow-hidden rounded-lg border-2 border-blue-500">
                    <img src={banner.url} alt={banner.fileName} className="h-full w-full bg-[#f5f5f5] object-contain" />
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="flex shrink-0 gap-3 max-[640px]:justify-end">
            <button
              className="cursor-pointer rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-[14px] font-medium text-gray-500 transition-all duration-200 ease-[ease] hover:bg-gray-100 hover:text-gray-800 max-[768px]:w-full max-[768px]:justify-center"
              onClick={onClose}
            >
              キャンセル
            </button>
            <button
              className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border-none bg-blue-500 px-6 py-2.5 text-[14px] font-medium text-white transition-all duration-200 ease-[ease] hover:bg-blue-600 disabled:cursor-not-allowed disabled:bg-gray-400 max-[768px]:w-full max-[768px]:justify-center"
              onClick={handleConfirm}
              disabled={selectedBanners.length === 0}
            >
              この画像を使う ({selectedBanners.length})
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BannerLibraryModal;
